import React, { useState, useEffect, useCallback } from 'react'
import { Youtube, Search, Clock, Eye, ThumbsUp, ArrowLeft, Loader2, ChevronDown, ExternalLink } from 'lucide-react'
import { youtubeApi } from '../api/client'

interface ChannelVideo {
  video_id: string
  title: string
  description: string
  thumbnail: string
  duration: number
  duration_formatted: string
  published_at: string
  view_count: number
  like_count: number
  comment_count: number
  url: string
}

interface YouTubeChannelPickerProps {
  onSelectVideo: (video: ChannelVideo) => void
  onClose: () => void
}

export function YouTubeChannelPicker({ onSelectVideo, onClose }: YouTubeChannelPickerProps) {
  const [isConnected, setIsConnected] = useState<boolean | null>(null)
  const [channelEmail, setChannelEmail] = useState('')
  const [videos, setVideos] = useState<ChannelVideo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState<'date' | 'viewCount' | 'title'>('date')
  const [nextPageToken, setNextPageToken] = useState<string | null>(null)
  const [totalResults, setTotalResults] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)

  // Check connection on mount
  useEffect(() => {
    checkConnection()
  }, [])

  const checkConnection = async () => {
    try {
      const status = await youtubeApi.getStatus()
      setIsConnected(status.connected)
      setChannelEmail(status.email || '')
      if (status.connected) {
        loadVideos()
      }
    } catch (err) {
      setIsConnected(false)
    }
  }

  const loadVideos = useCallback(async (pageToken?: string, append = false) => {
    if (append) {
      setLoadingMore(true)
    } else {
      setIsLoading(true)
      setVideos([])
    }
    setError('')

    try {
      const data = await youtubeApi.listChannelVideos({
        maxResults: 20,
        pageToken: pageToken || '',
        query: searchQuery,
        order: sortOrder,
      })

      if (append) {
        setVideos(prev => [...prev, ...data.videos])
      } else {
        setVideos(data.videos)
      }
      setNextPageToken(data.next_page_token)
      setTotalResults(data.total_results)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load videos')
    } finally {
      setIsLoading(false)
      setLoadingMore(false)
    }
  }, [searchQuery, sortOrder])

  // Reload when sort changes
  useEffect(() => {
    if (isConnected) {
      loadVideos()
    }
  }, [sortOrder])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    loadVideos()
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const formatCount = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
    return n.toString()
  }

  // Not connected — show connect prompt
  if (isConnected === false) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Youtube className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Connect Your YouTube Channel</h2>
          <p className="text-gray-400 mb-6">
            Link your channel to browse your videos, create clips, and publish Shorts — all from one place.
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={onClose} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors">
              Cancel
            </button>
            <a
              href={youtubeApi.getConnectUrl()}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 font-medium"
            >
              <Youtube className="w-4 h-4" />
              Connect Channel
            </a>
          </div>
        </div>
      </div>
    )
  }

  // Loading initial check
  if (isConnected === null) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-2xl p-8 text-center">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-3" />
          <p className="text-gray-400">Checking YouTube connection...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Youtube className="w-5 h-5 text-red-500" />
                My YouTube Videos
              </h2>
              <p className="text-sm text-gray-500">{channelEmail} · Select a video to create clips</p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {totalResults > 0 && `${totalResults} videos`}
          </div>
        </div>

        {/* Search + Sort */}
        <div className="px-6 py-3 border-b border-gray-700/50 flex gap-3">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your videos..."
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </form>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as any)}
            className="px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
          >
            <option value="date">Newest</option>
            <option value="viewCount">Most Viewed</option>
            <option value="title">Title A-Z</option>
          </select>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Video Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-20">
              <Youtube className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">
                {searchQuery ? 'No videos found for that search.' : 'No videos found on your channel.'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((video) => (
                  <button
                    key={video.video_id}
                    onClick={() => onSelectVideo(video)}
                    className="group bg-gray-900 rounded-xl overflow-hidden border border-gray-700/50 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 transition-all text-left"
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-gray-950">
                      {video.thumbnail ? (
                        <img src={video.thumbnail} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Youtube className="w-10 h-10 text-gray-700" />
                        </div>
                      )}
                      {/* Duration badge */}
                      <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded font-mono">
                        {video.duration_formatted}
                      </span>
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-lg">
                          Create Clips →
                        </span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-white line-clamp-2 mb-2 group-hover:text-indigo-300 transition-colors">
                        {video.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {formatCount(video.view_count)}
                        </span>
                        <span className="flex items-center gap-1">
                          <ThumbsUp className="w-3 h-3" />
                          {formatCount(video.like_count)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(video.published_at)}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Load More */}
              {nextPageToken && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={() => loadVideos(nextPageToken, true)}
                    disabled={loadingMore}
                    className="px-6 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                    Load More Videos
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
